/**
 * User-visible strings for the embed iframe.
 *
 * The embed runs on customer sites worldwide; we deliberately don't bundle
 * i18next here (would bloat the chat-only build that already ships ~90 KB
 * gzipped). Instead, the customer's loader can pass an override bag via
 * `agents/init`'s `strings` payload. Defaults are PT-BR because that is
 * still the primary deployment target; a customer in another locale ships
 * the override map in their snippet.
 *
 * Keep keys flat: nested objects make the parent-bridge decoder more painful
 * for marginal benefit. `runningTool` and `errorTool` are templates with a
 * `{name}` placeholder that the renderer substitutes — we don't pull a real
 * ICU runtime in for a single placeholder per string.
 */
export type EmbedStrings = {
  loading: string;
  errorLoadingChat: string;

  // Empty state in the message list (before the user has sent anything).
  emptyPrompt: string;

  // Chat-level errors.
  errorRateLimited: string;
  errorOriginMismatch: string;
  errorGeneric: string;

  // Composer.
  composerPlaceholder: string;
  composerLabel: string;
  composerSendLabel: string;

  // Header close button.
  closeLabel: string;

  // Shown when the conversation was handed off to a human operator. The reply
  // is async (support may be one person, offline) — copy must NOT promise a
  // live answer.
  humanModeNotice: string;

  // Typing indicator (assistant streaming). `thinkingVerbs` is a
  // comma-separated list cycled every few seconds while the model is busy —
  // playful by design (Claude-Code-ish), not load-bearing UX. Customers can
  // override to a single verb (no commas) to disable cycling, or to a
  // localized list for non-PT-BR audiences.
  thinkingVerbs: string;
  // Suffix appended after the seconds counter, e.g. "5s". Kept as a separate
  // key so RTL or non-"s" locales (e.g. Japanese 秒) can localize.
  thinkingTimerSuffix: string;

  // Tool execution status pills + confirm card.
  runningTool: string; // e.g. "Executando {name}…"
  doneTool: string; // e.g. "{name} concluído"
  errorToolPrefix: string; // shown when LLM-side tool error happens, e.g. "Não foi possível executar a ação"
  cancelledTool: string; // friendly version of "user cancelled" — NOT framed as an error.
  // KB-search tools are auto-generated server-side as `search_<kb-slug>`. The
  // slug is internal — we never want it surfaced to end users. The "running"
  // pill uses this string while the LLM is producing query args; the "done"
  // label is built server-side and shipped in the tool output (e.g.
  // "Encontrados 3 documentos relacionados") so it stays in sync with the
  // actual retrieval count without an extra round trip. No `{name}`
  // placeholder by design.
  runningKnowledgeSearch: string; // e.g. "Buscando conhecimento…"

  // Tool confirm card.
  confirmTitle: string; // template "Executar {name}?"
  confirmConfirm: string;
  confirmCancel: string;
  confirmBusy: string;
  confirmCardLabel: string; // aria-label, template "Confirmar ação {name}"

  // App-level error reasons (mapped from HandshakeError code).
  reasonInvalidAgentKey: string;
  reasonInvalidKey: string;
  reasonOriginNotAllowed: string;
  reasonOriginRequired: string;
  reasonOriginMismatch: string;
  reasonRateLimited: string;
  reasonAgentNotFound: string;
  reasonSessionExpired: string;
  reasonNetwork: string;
  reasonUnknown: string;
};

export const DEFAULT_STRINGS: EmbedStrings = {
  loading: 'Carregando…',
  errorLoadingChat: 'Não foi possível carregar o chat.',
  emptyPrompt: 'Pergunte algo pra começar.',

  errorRateLimited: 'Limite de uso excedido. Aguarde um pouco.',
  errorOriginMismatch: 'Sessão inconsistente. Recarregue a página.',
  errorGeneric: 'Algo deu errado. Tente novamente.',

  composerPlaceholder: 'Escreva sua mensagem…',
  composerLabel: 'Mensagem',
  composerSendLabel: 'Enviar',

  closeLabel: 'Fechar chat',

  humanModeNotice: 'Recebemos sua mensagem. Um atendente vai responder.',

  thinkingVerbs:
    'Pensando,Refletindo,Analisando,Calculando,Estruturando,Compondo,Sintetizando,Lapidando,Tecendo,Destrinchando,Costurando,Garimpando,Cruzando,Conectando,Ponderando',
  thinkingTimerSuffix: 's',

  runningTool: 'Executando {name}…',
  doneTool: '{name} concluído',
  errorToolPrefix: 'Não foi possível executar a ação.',
  cancelledTool: 'Ação cancelada.',
  runningKnowledgeSearch: 'Buscando conhecimento…',

  confirmTitle: 'Executar {name}?',
  confirmConfirm: 'Confirmar',
  confirmCancel: 'Cancelar',
  confirmBusy: 'Executando…',
  confirmCardLabel: 'Confirmar ação {name}',

  reasonInvalidAgentKey: 'Chave do agente inválida.',
  reasonInvalidKey: 'Chave revogada ou inexistente.',
  reasonOriginNotAllowed: 'Este site não está autorizado a usar esta chave.',
  reasonOriginRequired: 'Não foi possível identificar o site hospedeiro.',
  reasonOriginMismatch: 'Inconsistência de origem do site hospedeiro.',
  reasonRateLimited: 'Limite de uso excedido. Tente novamente em alguns minutos.',
  reasonAgentNotFound: 'Agente não encontrado.',
  reasonSessionExpired: 'Sessão expirou. Recarregue a página.',
  reasonNetwork: 'Falha de conexão.',
  reasonUnknown: 'Erro desconhecido.',
};

export function mergeStrings(overrides?: Partial<EmbedStrings>): EmbedStrings {
  if (!overrides) return DEFAULT_STRINGS;
  return { ...DEFAULT_STRINGS, ...overrides };
}

export function fillTemplate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? `{${key}}`);
}

/**
 * Validate a customer-supplied strings bag at the postMessage boundary.
 * Drops anything non-string. Customers can override only known keys; unknown
 * keys are ignored silently (so future additions don't break older snippets).
 */
export function decodeStringsOverride(raw: unknown): Partial<EmbedStrings> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Partial<EmbedStrings> = {};
  const known = Object.keys(DEFAULT_STRINGS) as (keyof EmbedStrings)[];
  for (const key of known) {
    const v = (raw as Record<string, unknown>)[key];
    if (typeof v === 'string' && v.length > 0) out[key] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
